import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

/* ──────────────────────────────────────────────────────────────
   DESIGN TOKENS (Match TrsLaboratorium & Ultra-Compact SIMRS UI)
────────────────────────────────────────────────────────────── */
const T = {
  primary:     '#0d9488', // Teal
  primaryDk:   '#0f766e',
  primaryLt:   '#ccfbf1',
  topHeaderBg: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
  headerBg:    '#f8fafc',
  headerBgSub: '#f1f5f9',
  headerText:  '#1e293b',
  headerBorder:'#cbd5e1',
  danger:      '#dc2626',
  dangerDk:    '#b91c1c',
  warn:        '#d97706',
  info:        '#0284c7',
  infoLt:      '#e0f2fe',
  slate50:     '#f8fafc',
  slate100:    '#f1f5f9',
  slate200:    '#e2e8f0',
  slate300:    '#cbd5e1',
  slate500:    '#64748b',
  slate600:    '#475569',
  slate700:    '#334155',
  slate800:    '#1e293b',
  white:       '#ffffff',
  border:      '#cbd5e1',
  navy:        '#0f172a',
  blue:        '#1e3a5f',
  blueHeader:  '#003366',
  blueBar:     '#004080',
};

const fmtRp = (v) => {
  const num = parseFloat(v) || 0;
  return new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
};

const S = {
  input: {
    border: `1px solid ${T.border}`, borderRadius: '3px',
    fontSize: '0.73rem', padding: '1px 6px', outline: 'none',
    background: T.white, color: T.slate800, height: '24px',
    transition: 'border-color .15s, box-shadow .15s',
  },
  btnPrimary: {
    background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDk} 100%)`,
    border: 'none', borderRadius: '4px', color: T.white, fontWeight: 700,
    fontSize: '0.71rem', padding: '2px 10px', cursor: 'pointer', height: '24px',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
    transition: 'opacity .15s',
  },
  btnDanger: {
    background: `linear-gradient(135deg, ${T.danger} 0%, ${T.dangerDk} 100%)`,
    border: 'none', borderRadius: '4px', color: T.white, fontWeight: 700,
    fontSize: '0.71rem', padding: '2px 10px', cursor: 'pointer', height: '24px',
    display: 'inline-flex', alignItems: 'center', gap: '4px',
  },
  btnGhost: {
    background: 'transparent', border: `1px solid ${T.border}`, borderRadius: '4px',
    color: T.slate600, fontWeight: 600, fontSize: '0.7rem', padding: '1px 7px', height: '22px',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '3px',
  },
  card: {
    background: T.white, borderRadius: '4px',
    border: `1px solid ${T.border}`, boxShadow: '0 1px 2px rgba(0,0,0,.04)',
  }
};

const PembuatanTagihan = () => {
  const navigate = useNavigate();
  const getApiUrl = () => localStorage.getItem('api_url') || 'http://202.155.94.169:8080';
  const getToken = () => localStorage.getItem('token');

  // Filter Form States
  const [typeJamin, setTypeJamin] = useState('1'); // 1=External, 2=Dokter, 3=Karyawan
  const [kdPrs, setKdPrs] = useState('');
  const [nmPrs, setNmPrs] = useState('');

  // Default dates: today
  const getTodayISO = () => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  };

  const [periode1, setPeriode1] = useState(getTodayISO());
  const [periode2, setPeriode2] = useState(getTodayISO());
  const [semuaTgl, setSemuaTgl] = useState(false);

  // Table Data & Selection
  const [listTagihan, setListTagihan] = useState([]);
  const [selectedNotas, setSelectedNotas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTable, setSearchTable] = useState('');

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  // Clock
  const [clock, setClock] = useState({ date: '', time: '' });

  // Live Dropdown & Modal Penjamin Lookup (/show_penjamin_by_type)
  const [showLookupModal, setShowLookupModal] = useState(false);
  const [lookupList, setLookupList] = useState([]);
  const [lookupQuery, setLookupQuery] = useState('');
  const [isLoadingLookup, setIsLoadingLookup] = useState(false);
  
  // Inline dropdown
  const [showInlineDrop, setShowInlineDrop] = useState(false);
  const inlineDropRef = useRef(null);

  // Dialog "TANGGAL TAGIH DAN CETAK TAGIHAN" States
  const [showProsesModal, setShowProsesModal] = useState(false);
  const initDate = new Date();
  const [cmbtg, setCmbtg] = useState(String(initDate.getDate()).padStart(2, '0'));
  const [cmbb, setCmbb] = useState(String(initDate.getMonth() + 1).padStart(2, '0'));
  const [txtthn, setTxtthn] = useState(String(initDate.getFullYear()));
  const [lokasiFile, setLokasiFile] = useState('D:\\LAPORAN_TAGIHAN\\TAGIHAN.xlsx');
  const [progressPercent, setProgressPercent] = useState(0);

  // Toast
  const [toast, setToast] = useState({ show: false, type: 'success', msg: '' });

  const showToast = (msg, type = 'success') => {
    setToast({ show: true, type, msg });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 3500);
  };

  // Clock ticker
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setClock({
        date: now.toLocaleDateString('id-ID', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }),
        time: now.toLocaleTimeString('id-ID'),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Close inline dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (inlineDropRef.current && !inlineDropRef.current.contains(e.target)) {
        setShowInlineDrop(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset page when search or itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTable, itemsPerPage]);

  // Fetch Penjamin from /show_penjamin_by_type
  const fetchPenjaminLookup = async (type = typeJamin, search = '') => {
    setIsLoadingLookup(true);
    try {
      const res = await fetch(`${getApiUrl()}/show_penjamin_by_type`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          type_jamin: type,
          search: search
        })
      });
      const data = await res.json();
      if (Array.isArray(data.list_penjamin)) {
        setLookupList(data.list_penjamin);
      } else {
        setLookupList([]);
      }
    } catch (e) {
      console.warn('Error lookup penjamin:', e);
    } finally {
      setIsLoadingLookup(false);
    }
  };

  // Fetch Data Tagihan from Backend (/show_tagihan)
  const fetchTagihan = async (overrideKdPrs = kdPrs) => {
    const activeKd = overrideKdPrs !== undefined ? overrideKdPrs : kdPrs;
    if (!activeKd || !activeKd.trim()) {
      setListTagihan([]);
      setSelectedNotas([]);
      return;
    }

    setIsLoading(true);
    setSelectedNotas([]);
    setCurrentPage(1);
    try {
      const res = await fetch(`${getApiUrl()}/show_tagihan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          type_jamin: typeJamin,
          kd_prs: activeKd.trim(),
          periode1: periode1,
          periode2: periode2,
          semua_tgl: semuaTgl
        })
      });
      const data = await res.json();
      if (data.status === 1 || Array.isArray(data.list_tagihan)) {
        setListTagihan(data.list_tagihan || []);
        if ((data.list_tagihan || []).length === 0) {
          showToast('Tidak ada data piutang untuk penjamin dan filter yang dipilih.', 'warning');
        }
      } else {
        showToast(data.msg || 'Gagal memuat data tagihan.', 'danger');
        setListTagihan([]);
      }
    } catch (e) {
      showToast('Error memuat data: ' + e.message, 'danger');
      setListTagihan([]);
    } finally {
      setIsLoading(false);
    }
  };

  // When typeJamin changes: reload penjamin list & clear table
  useEffect(() => {
    fetchPenjaminLookup(typeJamin, '');
    if (kdPrs) {
      fetchTagihan(kdPrs);
    } else {
      setListTagihan([]);
      setSelectedNotas([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeJamin, semuaTgl]);

  const handleTypeJaminChange = (newType) => {
    setTypeJamin(newType);
    setKdPrs('');
    setNmPrs('');
    setListTagihan([]);
    setSelectedNotas([]);
    fetchPenjaminLookup(newType, '');
  };

  const handleOpenLookup = () => {
    setLookupQuery('');
    fetchPenjaminLookup(typeJamin, '');
    setShowLookupModal(true);
  };

  const handleSelectPenjamin = (p) => {
    setKdPrs(p.kode || '');
    setNmPrs(p.nama || '');
    setShowLookupModal(false);
    setShowInlineDrop(false);
    fetchTagihan(p.kode || '');
  };

  // Filtered rows by search term
  const filteredTagihan = useMemo(() => {
    if (!searchTable) return listTagihan;
    const q = searchTable.toLowerCase();
    return listTagihan.filter(item =>
      (item.no_nota || '').toLowerCase().includes(q) ||
      (item.nm_pasien || '').toLowerCase().includes(q) ||
      (item.no_medrec || '').toLowerCase().includes(q) ||
      (item.nm_prs || '').toLowerCase().includes(q)
    );
  }, [listTagihan, searchTable]);

  // Pagination slicing
  const totalPages = Math.max(1, Math.ceil(filteredTagihan.length / itemsPerPage));
  const pagedTagihan = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTagihan.slice(start, start + itemsPerPage);
  }, [filteredTagihan, currentPage, itemsPerPage]);

  // Handle Select All / Toggle Rows
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedNotas(filteredTagihan.map(item => item.no_nota));
    } else {
      setSelectedNotas([]);
    }
  };

  const toggleRow = (no_nota) => {
    setSelectedNotas(prev =>
      prev.includes(no_nota) ? prev.filter(x => x !== no_nota) : [...prev, no_nota]
    );
  };

  // Calculations (Sum of total column)
  const totalPiutang = useMemo(() => {
    return listTagihan.reduce((acc, cur) => acc + (parseFloat(cur.total) || 0), 0);
  }, [listTagihan]);

  const totalTagihan = useMemo(() => {
    return listTagihan
      .filter(item => selectedNotas.includes(item.no_nota))
      .reduce((acc, cur) => acc + (parseFloat(cur.total) || 0), 0);
  }, [listTagihan, selectedNotas]);

  // Open Dialog "TANGGAL TAGIH DAN CETAK TAGIHAN"
  const handleOpenProsesModal = () => {
    if (!kdPrs) {
      showToast('Pilih Perusahaan / Penjamin terlebih dahulu!', 'warning');
      return;
    }
    if (listTagihan.length === 0) {
      showToast('Tidak ada data piutang untuk diproses!', 'warning');
      return;
    }
    const now = new Date();
    const dStr = String(now.getDate()).padStart(2, '0');
    const mStr = String(now.getMonth() + 1).padStart(2, '0');
    const yStr = String(now.getFullYear());
    setCmbtg(dStr);
    setCmbb(mStr);
    setTxtthn(yStr);
    setLokasiFile(`D:\\LAPORAN_TAGIHAN\\TAGIHAN_${kdPrs}_${yStr}${mStr}${dStr}.xlsx`);
    setProgressPercent(0);
    setShowProsesModal(true);
  };

  // Execute Process inside Modal Dialog (/prosestagih)
  const handleExecuteProses = async () => {
    setIsProcessing(true);
    setProgressPercent(20);
    try {
      setTimeout(() => setProgressPercent(50), 200);
      const res = await fetch(`${getApiUrl()}/prosestagih`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          txtkdprs: kdPrs,
          txtnmprs: nmPrs,
          cmbtypejamin: typeJamin,
          tgl_tagih: `${cmbtg}/${cmbb}/${txtthn}`,
          lokasi_file: lokasiFile,
          selected_notas: selectedNotas
        })
      });
      setProgressPercent(80);
      const data = await res.json();
      setProgressPercent(100);
      if (data.status === 1) {
        showToast(data.msg || 'Proses pembuatan tagihan berhasil!', 'success');
        setTimeout(() => {
          setShowProsesModal(false);
          fetchTagihan(kdPrs);
        }, 500);
      } else {
        showToast(data.msg || 'Gagal memproses tagihan!', 'danger');
      }
    } catch (e) {
      showToast('Error: ' + e.message, 'danger');
    } finally {
      setIsProcessing(false);
    }
  };

  // Cetak Ulang Action
  const handleCetakUlang = () => {
    if (selectedNotas.length === 0) {
      showToast('Pilih setidaknya satu nota untuk dicetak!', 'warning');
      return;
    }
    showToast(`Mencetak ${selectedNotas.length} nota tagihan...`);
    window.print();
  };

  // Filtered lookup items in modal
  const filteredLookup = useMemo(() => {
    if (!lookupQuery) return lookupList.slice(0, 100);
    const q = lookupQuery.toLowerCase();
    return lookupList.filter(p =>
      (p.kode || '').toLowerCase().includes(q) || (p.nama || '').toLowerCase().includes(q)
    ).slice(0, 100);
  }, [lookupList, lookupQuery]);

  // Filtered items in inline dropdown
  const inlineFiltered = useMemo(() => {
    if (!nmPrs) return lookupList.slice(0, 20);
    const q = nmPrs.toLowerCase();
    return lookupList.filter(p =>
      (p.kode || '').toLowerCase().includes(q) || (p.nama || '').toLowerCase().includes(q)
    ).slice(0, 20);
  }, [lookupList, nmPrs]);

  const getLabelPenjamin = () => {
    if (typeJamin === '2') return 'NAMA DOKTER RUMAH SAKIT';
    if (typeJamin === '3') return 'NAMA KARYAWAN';
    return 'NAMA PERUSAHAAN ';
  };

  // Pagination page buttons generator
  const renderPaginationButtons = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          style={{
            ...S.btnGhost,
            padding: '1px 6px', height: '22px', fontSize: '0.7rem', minWidth: '22px',
            background: currentPage === i ? T.primary : T.white,
            color: currentPage === i ? T.white : T.slate700,
            borderColor: currentPage === i ? T.primary : T.border,
            fontWeight: currentPage === i ? 800 : 600
          }}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  return (
    <div style={{
      background: T.slate100, height: '100vh', maxHeight: '100vh', overflow: 'hidden',
      display: 'flex', flexDirection: 'column', fontFamily: "'Inter','Segoe UI',sans-serif"
    }}>

      {/* TOAST NOTIFICATION */}
      {toast.show && (
        <div style={{
          position: 'fixed', top: '8px', right: '12px', zIndex: 9999,
          background: toast.type === 'success' ? '#022c22' : toast.type === 'warning' ? '#451a03' : '#450a0a',
          color: T.white, borderRadius: '4px', padding: '6px 12px',
          boxShadow: '0 4px 12px rgba(0,0,0,.2)',
          display: 'flex', alignItems: 'center', gap: '6px', minWidth: '240px',
          borderLeft: `3px solid ${toast.type === 'success' ? '#10b981' : toast.type === 'warning' ? '#f59e0b' : '#ef4444'}`,
          animation: 'slideIn .2s ease',
        }}>
          <i className={`bi ${toast.type === 'success' ? 'bi-check-circle-fill' : toast.type === 'warning' ? 'bi-exclamation-triangle-fill' : 'bi-x-circle-fill'}`}
            style={{ fontSize: '0.85rem', color: toast.type === 'success' ? '#10b981' : toast.type === 'warning' ? '#f59e0b' : '#ef4444' }} />
          <span style={{ fontSize: '0.74rem', fontWeight: 600 }}>{toast.msg}</span>
        </div>
      )}

      {/* TOPBAR HEADER (COMPACT) */}
      <div style={{ background: T.topHeaderBg, borderBottom: `1.5px solid ${T.primaryDk}`, boxShadow: '0 1px 3px rgba(13, 148, 136, 0.2)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '34px', padding: '0 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '22px', height: '22px', borderRadius: '4px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <i className="bi bi-receipt-cutoff" style={{ fontSize: '0.8rem', color: T.white }} />
            </div>
            <div>
              <span style={{ color: T.white, fontWeight: 800, fontSize: '0.8rem', letterSpacing: '0.3px' }}>
                PEMBUATAN TAGIHAN
              </span>
              <span className="d-none d-md-inline" style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.66rem', marginLeft: '6px' }}>
                — Modul Pembuatan Kuitansi &amp; Penagihan Piutang
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: '#ccfbf1', fontSize: '0.66rem', fontWeight: 600 }}>{clock.date}</span>
              <span style={{ color: T.white, fontSize: '0.72rem', fontWeight: 800, fontFamily: 'monospace' }}>{clock.time} WIB</span>
            </div>
            <button
              onClick={() => navigate('/home')}
              style={{ ...S.btnGhost, height: '22px', padding: '1px 6px', fontSize: '0.68rem', borderColor: 'rgba(255,255,255,0.4)', color: T.white, background: 'rgba(255,255,255,0.15)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            >
              <i className="bi bi-arrow-left" /> Kembali
            </button>
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER (FLEX COLUMN FULL HEIGHT) */}
      <div style={{ padding: '4px 8px', flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minHeight: 0 }}>

        {/* ── TOP FORM FILTER (COMPACT 1-PAGE STYLE) ── */}
        <div style={{
          ...S.card,
          background: T.white,
          border: `1px solid ${T.border}`,
          overflow: 'visible',
          flexShrink: 0
        }}>
          {/* Header Title Bar */}
          <div style={{
            background: 'linear-gradient(90deg, #003366 0%, #004080 100%)',
            color: T.white, padding: '3px 8px',
            textAlign: 'center', fontWeight: 800, fontSize: '0.72rem', letterSpacing: '0.4px',
            borderRadius: '3px 3px 0 0'
          }}>
            PEMBUATAN TAGIHAN
          </div>

          <div style={{ padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: '3px' }}>

            {/* Row 1: TIPE JAMINAN */}
            <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', alignItems: 'center', gap: '6px' }}>
              <div style={{
                background: '#004080', color: T.white, padding: '2px 8px',
                fontSize: '0.68rem', fontWeight: 700, borderRadius: '3px', letterSpacing: '0.2px'
              }}>
                TIPE JAMINAN
              </div>
              <div>
                <select
                  value={typeJamin}
                  onChange={e => handleTypeJaminChange(e.target.value)}
                  style={{ ...S.input, width: '100%', fontWeight: 600, height: '23px', fontSize: '0.72rem' }}
                >
                  <option value="1">1. External Perusahaan Rekanan</option>
                  <option value="2">2. Internal Dokter Rumah Sakit</option>
                  <option value="3">3. Internal Karyawan</option>
                </select>
              </div>
            </div>

            {/* Row 2: NAMA PERUSAHAAN PENJAMIN WITH LIVE AUTOCOMPLETE */}
            <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', alignItems: 'center', gap: '6px' }}>
              <div style={{
                background: '#004080', color: T.white, padding: '2px 8px',
                fontSize: '0.68rem', fontWeight: 700, borderRadius: '3px', letterSpacing: '0.2px'
              }}>
                {getLabelPenjamin()}
              </div>
              <div style={{ display: 'flex', gap: '4px', position: 'relative' }} ref={inlineDropRef}>
                <input
                  type="text"
                  placeholder="Kode..."
                  value={kdPrs}
                  onChange={e => {
                    const val = e.target.value.toUpperCase();
                    setKdPrs(val);
                    const match = lookupList.find(p => p.kode.toUpperCase() === val);
                    if (match) setNmPrs(match.nama);
                  }}
                  onBlur={() => {
                    if (kdPrs) fetchTagihan(kdPrs);
                    else {
                      setListTagihan([]);
                      setSelectedNotas([]);
                    }
                  }}
                  style={{ ...S.input, width: '90px', fontFamily: 'monospace', fontWeight: 700, height: '23px', fontSize: '0.72rem' }}
                />
                <div style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    placeholder={`Ketik pencarian ${getLabelPenjamin().toLowerCase()}...`}
                    value={nmPrs}
                    onFocus={() => setShowInlineDrop(true)}
                    onChange={e => {
                      const val = e.target.value;
                      setNmPrs(val);
                      setShowInlineDrop(true);
                      fetchPenjaminLookup(typeJamin, val);
                    }}
                    style={{ ...S.input, width: '100%', fontWeight: 600, height: '23px', fontSize: '0.72rem' }}
                  />
                  {/* Inline Autocomplete Dropdown */}
                  {showInlineDrop && inlineFiltered.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050,
                      background: T.white, border: `1px solid ${T.slate300}`, borderRadius: '4px',
                      boxShadow: '0 6px 14px rgba(0,0,0,.15)', maxHeight: '180px', overflowY: 'auto',
                      marginTop: '2px'
                    }}>
                      {inlineFiltered.map((p, idx) => (
                        <div
                          key={p.kode || idx}
                          onClick={() => handleSelectPenjamin(p)}
                          style={{
                            padding: '4px 8px', cursor: 'pointer', borderBottom: `1px solid ${T.slate100}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.71rem'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = T.primaryLt}
                          onMouseLeave={e => e.currentTarget.style.background = T.white}
                        >
                          <div>
                            <span style={{ fontFamily: 'monospace', fontWeight: 800, color: T.primaryDk, marginRight: '6px' }}>
                              [{p.kode}]
                            </span>
                            <span style={{ fontWeight: 600, color: T.slate800 }}>
                              {p.nama}
                            </span>
                          </div>
                          <span style={{ fontSize: '0.65rem', color: T.slate500 }}>
                            {p.keterangan || ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleOpenLookup}
                  style={{
                    border: `1px solid ${T.border}`, borderRadius: '3px',
                    background: T.slate100, color: T.slate700,
                    padding: '0 8px', height: '23px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.71rem', fontWeight: 600,
                    flexShrink: 0
                  }}
                  title="Cari dari show_penjamin_by_type"
                >
                  <i className="bi bi-search" /> Cari
                </button>
              </div>
            </div>

            {/* Row 3: PERIODE TRANSAKSI */}
            <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', alignItems: 'center', gap: '6px' }}>
              <div style={{
                background: '#004080', color: T.white, padding: '2px 8px',
                fontSize: '0.68rem', fontWeight: 700, borderRadius: '3px', letterSpacing: '0.2px'
              }}>
                PERIODE TRANSAKSI
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <input
                  type="date"
                  value={periode1}
                  disabled={semuaTgl}
                  onChange={e => setPeriode1(e.target.value)}
                  style={{ ...S.input, width: '125px', opacity: semuaTgl ? 0.6 : 1, height: '23px', fontSize: '0.72rem' }}
                />
                <span style={{
                  background: '#004080', color: T.white, padding: '1px 6px',
                  borderRadius: '3px', fontSize: '0.65rem', fontWeight: 800
                }}>
                  S/D
                </span>
                <input
                  type="date"
                  value={periode2}
                  disabled={semuaTgl}
                  onChange={e => setPeriode2(e.target.value)}
                  style={{ ...S.input, width: '125px', opacity: semuaTgl ? 0.6 : 1, height: '23px', fontSize: '0.72rem' }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '0.71rem', fontWeight: 700, color: T.slate700, margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={semuaTgl}
                    onChange={e => setSemuaTgl(e.target.checked)}
                    style={{ cursor: 'pointer', width: '13px', height: '13px' }}
                  />
                  SEMUA TANGGAL
                </label>
                <button
                  type="button"
                  onClick={() => {
                    if (!kdPrs) {
                      showToast('Pilih Perusahaan / Penjamin terlebih dahulu!', 'warning');
                      return;
                    }
                    fetchTagihan(kdPrs);
                  }}
                  disabled={isLoading}
                  style={{ ...S.btnPrimary, height: '23px', padding: '0 8px', fontSize: '0.71rem' }}
                >
                  {isLoading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-funnel-fill" />}
                  Tampilkan Data
                </button>
              </div>
            </div>

          </div>
        </div>

        {/* ── TABLE GRID SECTION WITH PAGINATION ── */}
        <div style={{ ...S.card, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* Table Toolbar (Compact) */}
          <div style={{
            padding: '3px 8px', borderBottom: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px',
            background: T.white, flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ position: 'relative' }}>
                <i className="bi bi-search" style={{ position: 'absolute', left: '6px', top: '50%', transform: 'translateY(-50%)', color: T.slate400, fontSize: '0.68rem', pointerEvents: 'none' }} />
                <input
                  type="text"
                  placeholder="Cari nota, pasien, RM..."
                  value={searchTable}
                  disabled={!kdPrs || listTagihan.length === 0}
                  onChange={e => setSearchTable(e.target.value)}
                  style={{ ...S.input, paddingLeft: '20px', width: '180px', height: '22px', fontSize: '0.7rem', opacity: !kdPrs ? 0.6 : 1 }}
                />
              </div>
              <span style={{ fontSize: '0.69rem', fontWeight: 700, color: T.slate600 }}>
                Terpilih: <span style={{ color: T.primary, fontWeight: 800 }}>{selectedNotas.length}</span> dari {filteredTagihan.length} Nota
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setSelectedNotas(filteredTagihan.map(i => i.no_nota))}
                disabled={!kdPrs || filteredTagihan.length === 0}
                style={{ ...S.btnGhost, fontSize: '0.68rem', padding: '1px 6px', height: '22px', opacity: (!kdPrs || filteredTagihan.length === 0) ? 0.5 : 1 }}
              >
                Pilih Semua
              </button>
              <button
                onClick={() => setSelectedNotas([])}
                disabled={selectedNotas.length === 0}
                style={{ ...S.btnGhost, fontSize: '0.68rem', padding: '1px 6px', height: '22px', opacity: selectedNotas.length === 0 ? 0.5 : 1 }}
              >
                Batal Pilih
              </button>
              <button
                onClick={() => {
                  if (kdPrs) fetchTagihan(kdPrs);
                }}
                disabled={isLoading || !kdPrs}
                style={{ ...S.btnGhost, fontSize: '0.68rem', padding: '1px 6px', height: '22px', opacity: (isLoading || !kdPrs) ? 0.5 : 1 }}
              >
                <i className={`bi bi-arrow-clockwise${isLoading ? ' spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>

          {/* Table Data (Scrollable Area) */}
          <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            <Table hover responsive style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', marginBottom: 0 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: T.headerBg, color: T.headerText }}>
                <tr style={{ borderBottom: `1.5px solid ${T.headerBorder}`, textAlign: 'center' }}>
                  <th style={{ width: '30px', padding: '4px 2px', borderRight: `1px solid ${T.headerBorder}` }}>
                    <input
                      type="checkbox"
                      disabled={filteredTagihan.length === 0}
                      checked={filteredTagihan.length > 0 && selectedNotas.length === filteredTagihan.length}
                      onChange={handleSelectAll}
                      style={{ cursor: filteredTagihan.length > 0 ? 'pointer' : 'default', width: '12px', height: '12px' }}
                    />
                  </th>
                  <th style={{ width: '38px', padding: '4px 2px', borderRight: `1px solid ${T.headerBorder}` }}>NO</th>
                  <th style={{ width: '95px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}` }}>NO NOTA</th>
                  <th style={{ width: '130px', padding: '4px 6px', borderRight: `1px solid ${T.headerBorder}`, textAlign: 'left' }}>PERUSAHAAN</th>
                  <th style={{ width: '80px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}` }}>TGL PULANG</th>
                  <th style={{ width: '75px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}` }}>NO MEDREC</th>
                  <th style={{ minWidth: '130px', padding: '4px 6px', borderRight: `1px solid ${T.headerBorder}`, textAlign: 'left' }}>NAMA PASIEN</th>
                  <th style={{ width: '110px', padding: '4px 6px', borderRight: `1px solid ${T.headerBorder}`, textAlign: 'left' }}>NAMA KARYAWAN</th>
                  <th style={{ width: '75px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}` }}>TGL TAGIH</th>
                  <th style={{ width: '85px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}`, textAlign: 'right' }}>TOTAL 2</th>
                  <th style={{ width: '70px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}` }}>ADM</th>
                  <th style={{ width: '85px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}`, textAlign: 'right' }}>TOTAL</th>
                  <th style={{ width: '80px', padding: '4px 4px', borderRight: `1px solid ${T.headerBorder}`, textAlign: 'right' }}>BAYAR</th>
                  <th style={{ width: '95px', padding: '4px 4px', textAlign: 'right', background: '#fef2f2', color: '#991b1b', fontWeight: 800 }}>SISA</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={14} style={{ textAlign: 'center', padding: '24px', color: T.slate400 }}>
                      <Spinner animation="border" size="sm" style={{ marginRight: '6px' }} /> Memuat data piutang tagihan...
                    </td>
                  </tr>
                ) : !kdPrs ? (
                  /* ── STATE: PERUSAHAAN BELUM DIPILIH ── */
                  <tr>
                    <td colSpan={14} style={{ textAlign: 'center', padding: '28px 12px', background: T.white }}>
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '36px', height: '36px', borderRadius: '50%',
                        background: '#e0f2fe', color: '#0284c7', marginBottom: '6px'
                      }}>
                        <i className="bi bi-building" style={{ fontSize: '1.2rem' }} />
                      </div>
                      <div style={{ fontWeight: 800, fontSize: '0.78rem', color: T.slate800, marginBottom: '2px' }}>
                        Perusahaan / Penjamin Belum Dipilih
                      </div>
                      <div style={{ fontSize: '0.7rem', color: T.slate500, maxWidth: '400px', margin: '0 auto' }}>
                        Silakan cari dan pilih <strong>{getLabelPenjamin().toLowerCase()}</strong> pada kolom isian di atas untuk menampilkan daftar piutang.
                      </div>
                    </td>
                  </tr>
                ) : pagedTagihan.length > 0 ? (
                  pagedTagihan.map((row, idx) => {
                    const rowNumber = (currentPage - 1) * itemsPerPage + idx + 1;
                    const isSelected = selectedNotas.includes(row.no_nota);
                    return (
                      <tr
                        key={row.no_nota || idx}
                        onClick={() => toggleRow(row.no_nota)}
                        style={{
                          background: isSelected ? '#f0fdf4' : idx % 2 === 0 ? T.white : T.slate50,
                          cursor: 'pointer', transition: 'background .1s'
                        }}
                      >
                        <td style={{ textAlign: 'center', padding: '2px 2px', borderRight: `1px solid ${T.slate100}` }} onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRow(row.no_nota)}
                            style={{ cursor: 'pointer', width: '12px', height: '12px' }}
                          />
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 2px', borderRight: `1px solid ${T.slate100}`, color: T.slate400, fontSize: '0.69rem' }}>
                          {rowNumber}
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, fontFamily: 'monospace', fontWeight: 800, color: T.primaryDk }}>
                          {row.no_nota}
                        </td>
                        <td style={{ padding: '2px 6px', borderRight: `1px solid ${T.slate100}`, color: T.slate700 }}>
                          {row.nm_prs || '-'}
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, color: T.slate600 }}>
                          {row.tgl_pulang || '-'}
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, fontFamily: 'monospace', fontWeight: 700 }}>
                          {row.no_medrec}
                        </td>
                        <td style={{ padding: '2px 6px', borderRight: `1px solid ${T.slate100}`, fontWeight: 600, color: T.slate800 }}>
                          {row.nm_pasien}
                        </td>
                        <td style={{ padding: '2px 6px', borderRight: `1px solid ${T.slate100}`, color: T.slate600 }}>
                          {row.nm_karyawan || '-'}
                        </td>
                        <td style={{ textAlign: 'center', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, color: T.slate600 }}>
                          {row.tgl_tagih || '-'}
                        </td>
                        <td style={{ textAlign: 'right', padding: '2px 4px', borderRight: `1px solid ${T.slate100}` }}>
                          {fmtRp(row.total2)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '2px 4px', borderRight: `1px solid ${T.slate100}` }}>
                          {fmtRp(row.adm)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, fontWeight: 700 }}>
                          {fmtRp(row.total)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '2px 4px', borderRight: `1px solid ${T.slate100}`, color: T.primaryDk }}>
                          {fmtRp(row.bayar)}
                        </td>
                        <td style={{ textAlign: 'right', padding: '2px 4px', fontWeight: 800, color: row.sisa > 0 ? T.dangerDk : T.slate600 }}>
                          {fmtRp(row.sisa)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  /* ── STATE: PERUSAHAAN DIPILIH TAPI TIDAK ADA DATA ── */
                  <tr>
                    <td colSpan={14} style={{ textAlign: 'center', padding: '28px 12px', color: T.slate400 }}>
                      <i className="bi bi-inbox" style={{ fontSize: '1.4rem', display: 'block', marginBottom: '4px' }} />
                      <div style={{ fontWeight: 700, fontSize: '0.76rem', color: T.slate700, marginBottom: '1px' }}>
                        Tidak Ada Data Piutang
                      </div>
                      <div style={{ fontSize: '0.68rem', color: T.slate500 }}>
                        Tidak ditemukan nota piutang belum ditagih untuk penjamin ini.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* ── PAGINATION BAR (COMPACT) ── */}
          {kdPrs && filteredTagihan.length > 0 && (
            <div style={{
              padding: '2px 8px', borderTop: `1px solid ${T.border}`,
              background: T.slate50, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: '4px', flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.68rem', color: T.slate600, fontWeight: 600 }}>
                  Menampilkan <strong>{Math.min((currentPage - 1) * itemsPerPage + 1, filteredTagihan.length)}</strong> - <strong>{Math.min(currentPage * itemsPerPage, filteredTagihan.length)}</strong> dari <strong>{filteredTagihan.length}</strong>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px', marginLeft: '4px' }}>
                  <span style={{ fontSize: '0.66rem', color: T.slate500 }}>Baris:</span>
                  <select
                    value={itemsPerPage}
                    onChange={e => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    style={{
                      border: `1px solid ${T.border}`, borderRadius: '3px',
                      fontSize: '0.68rem', padding: '0 2px', height: '18px', background: T.white, color: T.slate700
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              {/* Page Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  title="Halaman Pertama"
                  style={{ ...S.btnGhost, padding: '0 4px', height: '18px', fontSize: '0.65rem', opacity: currentPage === 1 ? 0.4 : 1 }}
                >
                  <i className="bi bi-chevron-double-left" />
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  title="Sebelumnya"
                  style={{ ...S.btnGhost, padding: '0 4px', height: '18px', fontSize: '0.65rem', opacity: currentPage === 1 ? 0.4 : 1 }}
                >
                  <i className="bi bi-chevron-left" />
                </button>

                {renderPaginationButtons()}

                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  title="Selanjutnya"
                  style={{ ...S.btnGhost, padding: '0 4px', height: '18px', fontSize: '0.65rem', opacity: currentPage === totalPages ? 0.4 : 1 }}
                >
                  <i className="bi bi-chevron-right" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Halaman Terakhir"
                  style={{ ...S.btnGhost, padding: '0 4px', height: '18px', fontSize: '0.65rem', opacity: currentPage === totalPages ? 0.4 : 1 }}
                >
                  <i className="bi bi-chevron-double-right" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── BOTTOM SUMMARY & ACTION BARS (COMPACT) ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flexShrink: 0 }}>

          {/* Blue Summary Bars */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {/* TOTAL PIUTANG */}
            <div style={{
              display: 'flex', alignItems: 'stretch',
              background: '#004080', borderRadius: '3px', overflow: 'hidden', height: '22px'
            }}>
              <div style={{
                color: T.white, fontWeight: 800, fontSize: '0.7rem',
                padding: '0 8px', display: 'flex', alignItems: 'center', flex: 1, letterSpacing: '0.3px'
              }}>
                TOTAL PIUTANG
              </div>
              <div style={{
                background: '#000000', color: '#22c55e', fontWeight: 900,
                fontSize: '0.78rem', padding: '0 10px', minWidth: '130px',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontFamily: 'monospace'
              }}>
                {fmtRp(totalPiutang)}
              </div>
            </div>

            {/* TOTAL TAGIHAN */}
            <div style={{
              display: 'flex', alignItems: 'stretch',
              background: '#004080', borderRadius: '3px', overflow: 'hidden', height: '22px'
            }}>
              <div style={{
                color: T.white, fontWeight: 800, fontSize: '0.7rem',
                padding: '0 8px', display: 'flex', alignItems: 'center', flex: 1, letterSpacing: '0.3px'
              }}>
                TOTAL TAGIHAN
              </div>
              <div style={{
                background: '#000000', color: '#38bdf8', fontWeight: 900,
                fontSize: '0.78rem', padding: '0 10px', minWidth: '130px',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', fontFamily: 'monospace'
              }}>
                {fmtRp(totalTagihan)}
              </div>
            </div>
          </div>

          {/* Action Buttons Row */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '6px', marginTop: '1px' }}>
            <button
              onClick={handleCetakUlang}
              disabled={selectedNotas.length === 0}
              style={{
                ...S.btnGhost,
                background: T.white, border: `1.5px solid #cbd5e1`,
                height: '24px', padding: '0 10px', fontSize: '0.71rem', fontWeight: 800,
                opacity: selectedNotas.length === 0 ? 0.6 : 1
              }}
            >
              <i className="bi bi-printer" /> <u>C</u>ETAK ULANG
            </button>

            <button
              onClick={handleOpenProsesModal}
              disabled={isProcessing || listTagihan.length === 0}
              style={{
                ...S.btnPrimary,
                height: '24px', padding: '0 12px', fontSize: '0.71rem', fontWeight: 800,
                opacity: (isProcessing || listTagihan.length === 0) ? 0.6 : 1
              }}
            >
              {isProcessing ? <Spinner animation="border" size="sm" /> : <i className="bi bi-gear-fill" />}
              <u>P</u>ROSES TAGIH
            </button>

            <button
              onClick={() => navigate('/home')}
              style={{
                ...S.btnDanger,
                height: '24px', padding: '0 12px', fontSize: '0.71rem', fontWeight: 800
              }}
            >
              <i className="bi bi-x-circle-fill" /> KELUAR
            </button>
          </div>

        </div>

      </div>

      {/* ── MODAL DIALOG: TANGGAL TAGIH DAN CETAK TAGIHAN (SESUAI GAMBAR) ── */}
      <Modal show={showProsesModal} onHide={() => !isProcessing && setShowProsesModal(false)} size="md" centered>
        <div style={{
          background: '#334155', borderRadius: '6px', overflow: 'hidden',
          border: '2px solid #004080', boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
        }}>
          {/* Header Title Bar */}
          <div style={{
            background: 'linear-gradient(90deg, #002b49 0%, #004080 100%)',
            color: T.white, padding: '6px 12px', textAlign: 'center',
            fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.5px'
          }}>
            TANGGAL TAGIH DAN CETAK TAGIHAN
          </div>

          <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>

            {/* Row 1: TANGGAL TAGIH (cmbtg, cmbb, txtthn) */}
            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', alignItems: 'center', gap: '6px' }}>
              <div style={{
                background: '#004080', color: T.white, padding: '3px 8px',
                fontSize: '0.72rem', fontWeight: 700, borderRadius: '3px', letterSpacing: '0.2px'
              }}>
                TANGGAL TAGIH
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {/* cmbtg */}
                <select
                  value={cmbtg}
                  onChange={e => setCmbtg(e.target.value)}
                  style={{ ...S.input, width: '55px', fontWeight: 700, textAlign: 'center' }}
                >
                  {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>

                {/* cmbb */}
                <select
                  value={cmbb}
                  onChange={e => setCmbb(e.target.value)}
                  style={{ ...S.input, width: '60px', fontWeight: 700, textAlign: 'center' }}
                >
                  {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>

                {/* txtthn */}
                <input
                  type="text"
                  value={txtthn}
                  onChange={e => setTxtthn(e.target.value)}
                  style={{ ...S.input, width: '70px', fontWeight: 700, textAlign: 'center', fontFamily: 'monospace' }}
                />
              </div>
            </div>

            {/* Row 2: LOKASI FILE LAPORAN */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              <div style={{
                background: '#004080', color: T.white, padding: '3px 8px',
                fontSize: '0.72rem', fontWeight: 700, borderRadius: '3px', letterSpacing: '0.2px', textAlign: 'center'
              }}>
                LOKASI FILE LAPORAN
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <input
                  type="text"
                  value={lokasiFile}
                  onChange={e => setLokasiFile(e.target.value)}
                  style={{ ...S.input, flex: 1, fontWeight: 600, fontSize: '0.72rem' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const fname = prompt('Tentukan nama/lokasi file laporan:', lokasiFile);
                    if (fname) setLokasiFile(fname);
                  }}
                  style={{
                    background: '#e2e8f0', border: '1px solid #cbd5e1', borderRadius: '3px',
                    padding: '2px 8px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center'
                  }}
                  title="Pilih lokasi file"
                >
                  <i className="bi bi-folder2-open" style={{ color: '#0284c7', fontSize: '0.85rem' }} />
                </button>
              </div>
            </div>

            {/* Row 3: PROGRESS BAR & ACTION BUTTONS */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px', gap: '10px' }}>
              {/* Progress Box */}
              <div style={{
                flex: 1, background: '#1e293b', border: '1px solid #475569', borderRadius: '3px',
                height: '26px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center'
              }}>
                <div style={{
                  width: `${progressPercent}%`, height: '100%',
                  background: 'linear-gradient(90deg, #0284c7 0%, #0d9488 100%)',
                  transition: 'width .3s ease'
                }} />
                <span style={{
                  position: 'absolute', width: '100%', textAlign: 'center',
                  fontSize: '0.72rem', fontWeight: 800, color: T.white, textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                }}>
                  {progressPercent}%
                </span>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button
                  type="button"
                  onClick={handleExecuteProses}
                  disabled={isProcessing}
                  style={{
                    ...S.btnPrimary,
                    background: 'linear-gradient(180deg, #f8fafc 0%, #cbd5e1 100%)',
                    color: '#0f172a', border: '1px solid #94a3b8',
                    height: '26px', padding: '0 12px', fontSize: '0.74rem', fontWeight: 800
                  }}
                >
                  {isProcessing ? (
                    <Spinner animation="border" size="sm" style={{ marginRight: '4px' }} />
                  ) : (
                    <i className="bi bi-gear-wide-connected me-1" style={{ color: '#0284c7' }} />
                  )}
                  <u>P</u>ROSES
                </button>

                <button
                  type="button"
                  onClick={() => setShowProsesModal(false)}
                  disabled={isProcessing}
                  style={{
                    ...S.btnDanger,
                    background: 'linear-gradient(180deg, #f8fafc 0%, #cbd5e1 100%)',
                    color: '#0f172a', border: '1px solid #94a3b8',
                    height: '26px', padding: '0 12px', fontSize: '0.74rem', fontWeight: 800
                  }}
                >
                  <i className="bi bi-x-circle-fill me-1" style={{ color: '#dc2626' }} />
                  <u>K</u>ELUAR
                </button>
              </div>
            </div>

          </div>
        </div>
      </Modal>

      {/* ── MODAL LOOKUP PENJAMIN / PERUSAHAAN (show_penjamin_by_type) ── */}
      <Modal show={showLookupModal} onHide={() => setShowLookupModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: T.topHeaderBg, color: T.white, padding: '6px 12px' }}>
          <Modal.Title style={{ color: T.white, fontSize: '0.8rem', fontWeight: 700 }}>
            <i className="bi bi-search me-2" />
            Cari {getLabelPenjamin()}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '8px', background: T.slate50 }}>
          <div style={{ position: 'relative', marginBottom: '6px' }}>
            <i className="bi bi-search" style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: T.slate400, fontSize: '0.74rem', pointerEvents: 'none' }} />
            <input
              autoFocus
              type="text"
              placeholder="Ketik kode atau nama untuk mencari..."
              value={lookupQuery}
              onChange={e => {
                const q = e.target.value;
                setLookupQuery(q);
                fetchPenjaminLookup(typeJamin, q);
              }}
              style={{ ...S.input, paddingLeft: '26px', height: '24px', width: '100%' }}
            />
          </div>
          <div style={{ maxHeight: '260px', overflowY: 'auto', border: `1px solid ${T.border}`, borderRadius: '4px', background: T.white }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.71rem' }}>
              <thead style={{ position: 'sticky', top: 0, background: T.headerBg, color: T.headerText, zIndex: 1 }}>
                <tr style={{ borderBottom: `1.5px solid ${T.headerBorder}` }}>
                  <th style={{ padding: '4px 8px', width: '80px', fontWeight: 700 }}>Kode</th>
                  <th style={{ padding: '4px 8px', fontWeight: 700 }}>Nama Penjamin</th>
                  <th style={{ padding: '4px 8px', width: '150px', fontWeight: 700 }}>Keterangan</th>
                  <th style={{ padding: '4px 8px', width: '60px', textAlign: 'center', fontWeight: 700 }}>Pilih</th>
                </tr>
              </thead>
              <tbody>
                {isLoadingLookup ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '18px', color: T.slate400 }}>
                      <Spinner animation="border" size="sm" /> Memuat data penjamin...
                    </td>
                  </tr>
                ) : filteredLookup.length > 0 ? (
                  filteredLookup.map((p, i) => (
                    <tr
                      key={p.kode || i}
                      style={{ background: i % 2 === 0 ? T.white : T.slate50, cursor: 'pointer', transition: 'background .1s' }}
                      onClick={() => handleSelectPenjamin(p)}
                      onMouseEnter={e => e.currentTarget.style.background = T.primaryLt}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? T.white : T.slate50}
                    >
                      <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontWeight: 800, color: T.primaryDk }}>
                        {p.kode}
                      </td>
                      <td style={{ padding: '4px 8px', fontWeight: 600, color: T.slate800 }}>
                        {p.nama}
                      </td>
                      <td style={{ padding: '4px 8px', color: T.slate600, fontSize: '0.67rem' }}>
                        {p.keterangan || '-'}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            handleSelectPenjamin(p);
                          }}
                          style={{ ...S.btnPrimary, padding: '1px 6px', height: '18px', fontSize: '0.66rem', borderRadius: '3px' }}
                        >
                          Pilih
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '18px', color: T.slate400 }}>
                      <i className="bi bi-inbox" style={{ display: 'block', fontSize: '1.2rem', marginBottom: '2px' }} />
                      Tidak ada data yang cocok
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: '4px 8px', background: T.slate50, borderTop: `1px solid ${T.slate100}` }}>
          <button onClick={() => setShowLookupModal(false)} style={{ ...S.btnGhost, height: '22px', padding: '0 8px', fontSize: '0.68rem' }}>
            <i className="bi bi-x-lg" /> Tutup
          </button>
        </Modal.Footer>
      </Modal>

      <style>{`
        @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
        .spin { animation: spin .8s linear infinite; }
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default PembuatanTagihan;
